"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { BedDouble, Bath, Home, MapPin, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPrimaryPropertyImageUrl, shouldUnoptimizedPropertyImage } from "@/lib/property-image";
import { getPropertyHref } from "@/lib/property-url";

interface AreaProperty {
    id: string;
    title: string;
    price: number;
    location: string;
    location_data?: { id: string; name: string; short_name: string; slug: string } | null;
    bedrooms: number;
    bathrooms: number;
    area_sqft: number | null;
    images: string[] | null;
    cover_image: string[] | null;
    featured: boolean;
    property_type_id?: string | null;
    room_type_id?: string | null;
}

interface Option {
    value: string;
    label: string;
}

interface PriceRange {
    value: string;
    label: string;
    min: number;
    max: number;
}

const PRICE_RANGES: PriceRange[] = [
    { value: "0-3000", label: "Under AED 3,000", min: 0, max: 3000 },
    { value: "3001-5000", label: "AED 3,001 - 5,000", min: 3001, max: 5000 },
    { value: "5001-10000", label: "AED 5,001 - 10,000", min: 5001, max: 10000 },
    { value: "10001-20000", label: "AED 10,001 - 20,000", min: 10001, max: 20000 },
    { value: "20000+", label: "AED 20,000+", min: 20000, max: Infinity },
];

const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, "");

// The homepage search sends option labels, the properties list sends ids: accept either.
function resolveOption(options: Option[], raw: string): string {
    if (!raw) return "";
    return options.find((o) => o.value === raw || norm(o.label) === norm(raw))?.value ?? "";
}

// A price typed on the list page may not be one of the presets: keep it as its own option.
function priceFromQuery(min: string, max: string): PriceRange | null {
    if (!min && !max) return null;
    const lo = Number(min || 0);
    const hi = max ? Number(max) : Infinity;
    if (Number.isNaN(lo) || Number.isNaN(hi)) return null;
    const preset = PRICE_RANGES.find((r) => r.min === lo && r.max === hi);
    if (preset) return preset;
    const label = hi === Infinity ? `AED ${lo.toLocaleString()}+` : `AED ${lo.toLocaleString()} - ${hi.toLocaleString()}`;
    return { value: `${lo}-${max}`, label, min: lo, max: hi };
}

function getPropertyImage(property: AreaProperty): string | null {
    return getPrimaryPropertyImageUrl(property);
}

function formatPrice(price: number): string {
    if (price >= 1000000) {
        return `AED ${(price / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
    }
    if (price >= 1000) {
        return `AED ${(price / 1000).toFixed(1).replace(/\.0$/, "")}K`;
    }
    return `AED ${price.toLocaleString()}`;
}

const selectClass =
    "h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40";

export default function AreaPropertyGrid({
    properties,
    locationName,
    propertyTypes,
    roomTypes,
    initial,
}: {
    properties: AreaProperty[];
    locationName: string;
    propertyTypes: Option[];
    roomTypes: Option[];
    initial: { lookingFor: string; roomType: string; minPrice: string; maxPrice: string };
}) {
    const [lookingFor, setLookingFor] = useState(() => resolveOption(propertyTypes, initial.lookingFor));
    const [roomType, setRoomType] = useState(() => resolveOption(roomTypes, initial.roomType));
    const fromQuery = useMemo(() => priceFromQuery(initial.minPrice, initial.maxPrice), [initial.minPrice, initial.maxPrice]);
    const priceRanges = useMemo(
        () => (fromQuery && !PRICE_RANGES.includes(fromQuery) ? [fromQuery, ...PRICE_RANGES] : PRICE_RANGES),
        [fromQuery],
    );
    const [price, setPrice] = useState(fromQuery?.value ?? "");

    const shown = useMemo(() => {
        const range = priceRanges.find((r) => r.value === price);
        return properties.filter(
            (p) =>
                (!lookingFor || p.property_type_id === lookingFor) &&
                (!roomType || p.room_type_id === roomType) &&
                (!range || (p.price >= range.min && p.price <= range.max)),
        );
    }, [properties, lookingFor, roomType, price, priceRanges]);

    const filtered = Boolean(lookingFor || roomType || price);
    const clear = () => {
        setLookingFor("");
        setRoomType("");
        setPrice("");
    };

    if (properties.length === 0) {
        return (
            <div className="text-center py-16">
                <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No Properties Found</h3>
                <p className="text-muted-foreground mb-6">
                    We couldn&apos;t find any properties in {locationName} at the moment.
                </p>
                <Link href="/properties">
                    <Button variant="default">Browse All Properties</Button>
                </Link>
            </div>
        );
    }

    return (
        <>
            <div className="mb-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <select aria-label="Looking for" className={selectClass} value={lookingFor} onChange={(e) => setLookingFor(e.target.value)}>
                    <option value="">Looking for: Any</option>
                    {propertyTypes.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                <select aria-label="Type of room" className={selectClass} value={roomType} onChange={(e) => setRoomType(e.target.value)}>
                    <option value="">Type of room: Any</option>
                    {roomTypes.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                <select aria-label="Price per month" className={selectClass} value={price} onChange={(e) => setPrice(e.target.value)}>
                    <option value="">Price per month: Any</option>
                    {priceRanges.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                </select>
                <Button variant="outline" className="h-11 rounded-xl" disabled={!filtered} onClick={clear}>
                    Clear filters
                </Button>
            </div>

            {shown.length === 0 ? (
                <div className="text-center py-16">
                    <h3 className="text-xl font-semibold text-foreground mb-2">No properties match these filters</h3>
                    <p className="text-muted-foreground mb-6">Try a different room type or price range in {locationName}.</p>
                    <Button variant="default" onClick={clear}>Clear filters</Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {shown.map((property) => {
                        const propertyImage = getPropertyImage(property);

                        return (
                            <Link
                                                key={property.id}
                                                href={getPropertyHref(property)}
                                                className="group cursor-pointer"
                                            >
                                                <div className="relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2 bg-card">
                                                    <div className="relative h-72 overflow-hidden bg-muted flex items-center justify-center">
                                                        {propertyImage ? (
                                                            <Image
                                                                src={propertyImage}
                                                                alt={property.title}
                                                                fill
                                                                className="object-cover transition-transform duration-700 group-hover:scale-110"
                                                                unoptimized={shouldUnoptimizedPropertyImage(propertyImage)}
                                                            />
                                                        ) : (
                                                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                                                <MapPin className="w-8 h-8 opacity-20" />
                                                                <span className="text-sm font-medium">No images available</span>
                                                            </div>
                                                        )}
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                                                        {/* Featured Badge */}
                                                        {property.featured && (
                                                            <div className="absolute top-4 left-4">
                                                                <span className="bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                                                                    <Star className="w-3 h-3 fill-current" />
                                                                    Featured
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Property Details Overlay */}
                                                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-xs bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                                                                    <BedDouble className="w-3 h-3" />
                                                                    {property.bedrooms} BR
                                                                </span>
                                                                {property.bathrooms > 0 && (
                                                                    <span className="text-xs bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                                                                        <Bath className="w-3 h-3" />
                                                                        {property.bathrooms} Bath
                                                                    </span>
                                                                )}
                                                                {(property.area_sqft ?? 0) > 0 && (
                                                                    <span className="text-xs bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
                                                                        {property.area_sqft!.toLocaleString()} sqft
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <h3 className="text-xl font-bold group-hover:text-yellow-300 transition-colors mb-1 line-clamp-1">
                                                                {property.title}
                                                            </h3>
                                                            <div className="flex items-center gap-1 text-sm text-white/80 mb-2">
                                                                <MapPin className="w-3 h-3" />
                                                                {property.location_data?.short_name || property.location}
                                                            </div>
                                                            <p className="text-lg font-semibold text-yellow-400">
                                                                {formatPrice(property.price)}/month
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Link>
                        );
                    })}
                </div>
            )}
        </>
    );
}
